import { useEffect, useState } from "react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { api } from "@/lib/api"
import { pacificTime, reportMode, reportRoot, type ReferenceHistory } from "@/lib/pledge-submissions"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"

export function PledgeReportHistory({ benefitId }: { benefitId: string }) {
  const epoch = useAppSelector(state => state.staffSession.epoch)
  const canView = useAppSelector(state => hasPermission(state.permissions.permissions, "pledge-reports:view"))
  return canView ? <History key={`${epoch}/${benefitId}`} benefitId={benefitId} /> : null
}

function History({ benefitId }: { benefitId: string }) {
  const [value, setValue] = useState<ReferenceHistory | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    let active = true
    setBusy(true); setError(null); setValue(null)
    api.post<ReferenceHistory>(`${reportRoot}/applications/${benefitId}/history`, {}).then(result => { if (active) setValue(result) })
      .catch(e => { if (active) setError(e instanceof Error ? e.message : "Report history unavailable") })
      .finally(() => { if (active) setBusy(false) })
    return () => { active = false }
  }, [benefitId, revision])
  async function more() {
    if (busy || !value?.next) return
    setBusy(true); setError(null)
    try {
      const next = await api.post<ReferenceHistory>(`${reportRoot}/applications/${benefitId}/history`, { before: value.next })
      setValue({ ...next, reports: [...value.reports, ...next.reports] })
    } catch (e) { setError(e instanceof Error ? e.message : "Report history unavailable") }
    finally { setBusy(false) }
  }
  return <Card><CardHeader><CardTitle>Pledge report references</CardTitle><CardDescription>Pacific times (America/Los_Angeles). Generated is not submitted. Initial reconciliation is not proof of a past send.</CardDescription></CardHeader><CardContent className="space-y-3">
    <Button variant="outline" disabled={busy} onClick={() => setRevision(n => n + 1)}>Refresh report references</Button>
    {error && <p role="alert">{error}</p>}{busy && <p role="status">Loading report references…</p>}
    {value?.reports.map(report => <div className="rounded border p-3" key={report.reportId}><p>{reportMode(report.mode)} · {report.reportId}</p><p>Generated at: {pacificTime(report.generatedAt)}</p>
      {report.groups.map(group => <p key={group.recipientKey}>{group.recipientName} · Submitted at: {pacificTime(group.submittedAt)}{group.submissionSequence !== null && ` · Checkpoint ${group.submissionSequence}`}</p>)}
      {report.canReadWholeReport ? <Link className="underline" to={`/pledge-reports?report=${report.reportId}`}>Open frozen report</Link> : <p>Only this file's evidence is shown. The combined report contains other owners' files and cannot be opened here.</p>}
    </div>)}
    {value?.reports.length === 0 && <p>No report references for this file.</p>}
    {value?.next && <Button variant="outline" disabled={busy} onClick={() => void more()}>More report references</Button>}
  </CardContent></Card>
}
